/**
 * Bundle Optimization Utilities
 * Tools for dynamic imports, code splitting, and bundle analysis
 */

import React from 'react';

// Dynamic import with error handling
export const dynamicImport = async (importFn, retries = 3) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const module = await importFn();
      return module;
    } catch (error) {
      console.warn(`Failed to load module (attempt ${attempt}/${retries}):`, error);
      
      if (attempt === retries) {
        throw new Error(`Failed to load module after ${retries} attempts: ${error.message}`);
      }
      
      // Wait before retry with exponential backoff
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
    }
  }
};

// Preload critical resources
export const preloadCriticalResources = () => {
  const criticalModules = [
    () => import('../components/Common/MainNav'),
    () => import('../contexts/AuthContext'),
    () => import('../hooks/useSimpleBidSync')
  ];

  return Promise.allSettled(
    criticalModules.map(moduleImport => dynamicImport(moduleImport))
  );
};

// Lazy load non-critical resources
export const preloadNonCriticalResources = () => {
  const nonCriticalModules = [
    () => import('../components/Modals/PlayerHistoryModal'),
    () => import('../components/AuctionDraftRoom/DraftSettingsModal'),
    () => import('../components/Common/Footer')
  ];

  // Use requestIdleCallback for non-critical preloading
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      nonCriticalModules.forEach(moduleImport => 
        dynamicImport(moduleImport).catch(() => {
          // Silently fail for non-critical resources
        })
      );
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      nonCriticalModules.forEach(moduleImport => 
        dynamicImport(moduleImport).catch(() => {})
      );
    }, 2000);
  }
};

// Route-based code splitting
export const createRouteComponent = (importFn, fallback = null) => {
  const LazyComponent = React.lazy(() => dynamicImport(importFn));
  
  return React.forwardRef((props, ref) => (
    <React.Suspense fallback={fallback}>
      <LazyComponent {...props} ref={ref} />
    </React.Suspense>
  ));
};

// Bundle size analyzer (development only)
export const analyzeBundleSize = async () => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  try {
    // Estimate bundle sizes
    const estimates = {
      react: await estimateModuleSize(() => import('react')),
      reactDom: await estimateModuleSize(() => import('react-dom')),
      lucideReact: await estimateModuleSize(() => import('lucide-react')),
      authContext: await estimateModuleSize(() => import('../contexts/AuthContext')),
      mainApp: await estimateModuleSize(() => import('../App'))
    };

    console.table(estimates);
  } catch (error) {
    console.warn('Bundle analysis failed:', error);
  }
};

const estimateModuleSize = async (importFn) => {
  const startTime = performance.now();
  const startMemory = performance.memory?.usedJSHeapSize || 0;
  
  try {
    await importFn();
    const loadTime = performance.now() - startTime;
    const memoryUsed = (performance.memory?.usedJSHeapSize || 0) - startMemory;
    
    return {
      loadTime: `${loadTime.toFixed(2)}ms`,
      estimatedSize: `${(memoryUsed / 1024).toFixed(2)}KB`
    };
  } catch (error) {
    return {
      loadTime: 'Failed',
      estimatedSize: 'Failed'
    };
  }
};

// Module federation utilities (for micro-frontends)
export const createFederatedModule = (scope, module) => {
  return () => {
    return new Promise((resolve, reject) => {
      if (!window[scope]) {
        reject(new Error(`Federated module ${scope} not available`));
        return;
      }

      window[scope].get(module).then(factory => {
        const Module = factory();
        resolve(Module);
      }).catch(reject);
    });
  };
};

// Resource hints for better loading
export const addResourceHints = () => {
  const head = document.head;
  
  // DNS prefetch for external resources
  const dnsPrefetch = [
    'fonts.googleapis.com',
    'fonts.gstatic.com'
  ];
  
  dnsPrefetch.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = `//${domain}`;
    head.appendChild(link);
  });

  // Preload critical fonts
  const fonts = [
    '/fonts/inter-var.woff2'
  ];
  
  fonts.forEach(fontUrl => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/woff2';
    link.crossOrigin = 'anonymous';
    link.href = fontUrl;
    head.appendChild(link);
  });
};

// Service worker registration for caching
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('SW registered:', registration);
      
      // Update available
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New content available
            console.log('New content available; please refresh.');
          }
        });
      });
    } catch (error) {
      console.log('SW registration failed:', error);
    }
  }
};

// Critical CSS inlining
export const inlineCriticalCSS = (css) => {
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
};

// Performance metrics collection
export const collectPerformanceMetrics = () => {
  if ('performance' in window) {
    const metrics = {
      // Navigation timing
      navigation: performance.getEntriesByType('navigation')[0],
      
      // Resource timing
      resources: performance.getEntriesByType('resource'),
      
      // User timing
      userTiming: performance.getEntriesByType('measure'),
      
      // Memory usage (if available)
      memory: performance.memory ? {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
      } : null
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('Performance Metrics:', metrics);
    }

    return metrics;
  }
  
  return null;
};

// Web Vitals integration
export const reportWebVitals = (onPerfEntry) => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS(onPerfEntry);
      getFID(onPerfEntry);
      getFCP(onPerfEntry);
      getLCP(onPerfEntry);
      getTTFB(onPerfEntry);
    }).catch(() => {
      // web-vitals not available
    });
  }
};

export default {
  dynamicImport,
  preloadCriticalResources,
  preloadNonCriticalResources,
  createRouteComponent,
  analyzeBundleSize,
  createFederatedModule,
  addResourceHints,
  registerServiceWorker,
  inlineCriticalCSS,
  collectPerformanceMetrics,
  reportWebVitals
};